import { Component, effect, inject, signal, WritableSignal, Output, EventEmitter, computed } from '@angular/core';
import { BlockUI, BlockUIModule, NgBlockUI } from 'ng-block-ui';
import { first } from 'rxjs/operators';
import { ApiCallService } from '../../../../../services/api-call.service';
import { WeaveSelectSearchComponent } from '../../../../../shared/forms/weave-select-search/weave-select-search.component';
import { WeaveInputComponent } from '../../../../../shared/forms/weave-input/weave-input.component';
import { WeaveButtonComponent } from '../../../../../shared/forms/weave-button/weave-button.component';
import {ShortWeaveLoaderComponent} from "../../../../../shared/loaders/short-weave-loader/short-weave-loader.component";
import {RemoteResponse} from "../../../../../shared/remote-response";

@Component({
  selector: 'app-create-account',
  standalone: true,
  imports: [WeaveSelectSearchComponent, WeaveInputComponent, WeaveButtonComponent, BlockUIModule],
  templateUrl: './create-account.component.html',
  styleUrl: './create-account.component.scss'
})
export class CreateAccountComponent {
  private readonly apiService = inject(ApiCallService);

  @Output() accountCreated = new EventEmitter<string>();

  selectedOfficialCollectionRequestKey: WritableSignal<string> = signal('');
  selectedTemplateRequestKey: WritableSignal<string> = signal('');
  programName: WritableSignal<string> = signal('');
  institutionName: WritableSignal<string> = signal('');
  officialCollectionsOptions: { value: string, label: string }[] = [];
  templateOptions: { value: string, label: string }[] = [];
  errorMsg: WritableSignal<string> = signal('');
  isSelectedOfficialCollectionProgrammatic = computed(() => {
    return this.officialCollections?.find(oc => oc.requestKey === this.selectedOfficialCollectionRequestKey())?.isProgrammatic ?? false;
  });
  officialCollections: any[] = [];
  @BlockUI('createAccountUI') blockUI: NgBlockUI;
  protected readonly blockTemplate = ShortWeaveLoaderComponent;

  constructor() {
    this.getOfficialCollectionList();
    effect(() => {
      if (this.officialCollections && this.officialCollections.length > 0 && this.officialCollectionsOptions.length === 0) {
        this.officialCollectionsOptions = this.officialCollections.map(officialCollection => ({
          value: officialCollection.requestKey,
          label: officialCollection.name,
        }));
      }
      if (this.selectedOfficialCollectionRequestKey() && this.officialCollections.length > 0) {
        const found = this.officialCollections.find(oc => oc.requestKey === this.selectedOfficialCollectionRequestKey());
        this.templateOptions = found?.templates.map(template => ({
          value: template.requestKey,
          label: template.name,
        })) || [];
        if (this.templateOptions.length === 1) {
          this.selectedTemplateRequestKey.set(this.templateOptions[0].value);
        } else if (this.templateOptions.length === 0) {
          this.errorMsg.set('The selected accreditor does not have any templates available. Please contact support for assistance.');
        }
      }
    });
  }

  getOfficialCollectionList() {
    this.apiService.postJsonData('/public/getOfficialCollections', {}).pipe(first()).subscribe(response => {
      if (response && response.success) {
        const officialCollectionsList = response.data[0].dataSet.map((collection: any) => ({
          requestKey: collection.requestKey,
          name: collection.name,
          isProgrammatic: collection.isProgrammatic,
          templates: collection.templates.map((template: any) => ({
            name: template.name,
            requestKey: template.requestKey,
          })).sort((a: any, b: any) => a.name.localeCompare(b.name)),
        })).sort((a: any, b: any) => a.name.localeCompare(b.name));
        this.officialCollections = officialCollectionsList;
        this.officialCollectionsOptions = officialCollectionsList.map((officialCollection: any) => ({
          value: officialCollection.requestKey,
          label: officialCollection.name,
        }));
      } else {
        this.officialCollections = [];
        this.officialCollectionsOptions = [];
        this.errorMsg.set('Failed to load accreditors.');
      }
    });
  }

  setSelectedOfficialCollectionRequestKey(collectionRequestKey: string) {
    this.errorMsg.set('');
    this.selectedTemplateRequestKey.set('');
    this.programName.set('');
    this.institutionName.set('');
    this.selectedOfficialCollectionRequestKey.set(collectionRequestKey);
  }

  setSelectedTemplateRequestKey(templateRequestKey: string) {
    this.errorMsg.set('');
    this.programName.set('');
    this.institutionName.set('');
    this.selectedTemplateRequestKey.set(templateRequestKey);
  }

  setProgramName(programName: string) {
    this.errorMsg.set('');
    this.programName.set(programName);
    this.institutionName.set('');
  }

  setInstitutionName(institutionName: string) {
    this.errorMsg.set('');
    this.institutionName.set(institutionName);
    this.programName.set('');
  }

  onSave() {
    this.errorMsg.set('');
    if (!this.selectedOfficialCollectionRequestKey() || !this.selectedTemplateRequestKey() || (!this.programName() && !this.institutionName())) {
      this.errorMsg.set('Please fill all required fields.');
      return;
    }
    this.blockUI.start();
    const payload: any = {
      officialCollectionRequestKey: this.selectedOfficialCollectionRequestKey(),
      templateRequestKey: this.selectedTemplateRequestKey(),
      programName: this.isSelectedOfficialCollectionProgrammatic() ? this.programName() : undefined,
      institutionName: !this.isSelectedOfficialCollectionProgrammatic() ? this.institutionName() : undefined
    };
    this.apiService.postJsonData('/createAccount/', payload).pipe(first()).subscribe((response: RemoteResponse) => {
      this.blockUI.stop();
      if (response.success) {
        this.accountCreated.emit(response.data[0].dataSet.programRequestKey);
      } else {
        this.errorMsg.set(response.dataError[0].description || response.dataError[0].message || 'Account creation failed.');
      }
    });
  }

  onCancel() {
    this.accountCreated.emit(null);
  }
}
